import { useLazyQuery, useQuery } from '@apollo/client'
import RefreshIcon from '@mui/icons-material/Refresh'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import React, { useCallback, useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import {
  MediaAccessUrlQuery,
  MediaAccessUrlQueryVariables,
  mediaQuery,
  mediaQueryVariables,
} from '../../generated/graphql'
import Medias from '../../schema/Medias'
import GraphqlError from '../GraphqlError'
import Loading from '../Loading'

/** Minimal media metadata needed for display — can come from a parent list query. */
export interface MediaInfo {
  filename: string
  mimeType: string
  name: string
}

interface Props {
  idMedia: string
  showPreview?: boolean
  /**
   * Pre-fetched media metadata from a parent list query (e.g. GET_MEDIAS).
   * When provided, the internal GET_MEDIA network request is skipped entirely.
   */
  mediaData?: MediaInfo
  /**
   * When true, do not prefetch signed URLs on mount. The filename is shown immediately
   * and the URL is fetched only when the user clicks (or on explicit retry).
   */
  deferAccessUrl?: boolean
}

/** Refresh 30 s before the signed URL actually expires to avoid dead links. */
const URL_EXPIRY_BUFFER_MS = 30_000

function isUrlExpired(issued: number, expiration: number): boolean {
  return Date.now() > (issued + expiration) * 1000 - URL_EXPIRY_BUFFER_MS
}

const ACCESS_URL_DEBOUNCE_MS = 300

const useStyles = makeStyles()(() => {
  return {
    imageMediaPreview: {
      borderWidth: '2px',
      borderRadius: '2px',
      borderColor: '#EEEEEE',
      borderStyle: 'dashed',
      maxWidth: '100%',
      objectFit: 'contain',
    },
    urlErrorWrapper: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      opacity: 0.7,
    },
    nameWithLoadingUrl: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
    },
  }
})

export const MediaFieldView: React.FC<Props> = ({
  idMedia,
  showPreview = false,
  mediaData,
  deferAccessUrl = false,
}) => {
  const { classes } = useStyles()
  const [isOpeningUrl, setIsOpeningUrl] = useState(false)

  const { loading, error, data } = useQuery<mediaQuery, mediaQueryVariables>(Medias.GET_MEDIA, {
    variables: { id: idMedia },
    skip: !!mediaData,
  })

  const [executeAccessUrlQuery, { data: mediaUrlData, error: mediaUrlError, loading: mediaUrlLoading }] = useLazyQuery<
    MediaAccessUrlQuery,
    MediaAccessUrlQueryVariables
  >(Medias.GET_ACCESS_URL, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'none',
  })

  const fetchAccessUrl = useCallback(
    async (networkOnly = false) => {
      const result = await executeAccessUrlQuery({
        variables: { id: idMedia },
        fetchPolicy: networkOnly ? 'network-only' : 'cache-first',
      })
      return result.data?.mediaAccessUrl?.url
    },
    [executeAccessUrlQuery, idMedia],
  )

  const retryUrl = useCallback(() => {
    if (idMedia) {
      fetchAccessUrl(true).catch(() => {})
    }
  }, [fetchAccessUrl, idMedia])

  const openAccessUrl = useCallback(async () => {
    if (!idMedia || isOpeningUrl) {
      return
    }
    setIsOpeningUrl(true)
    try {
      const url = await fetchAccessUrl(true)
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    } finally {
      setIsOpeningUrl(false)
    }
  }, [fetchAccessUrl, idMedia, isOpeningUrl])

  useEffect(() => {
    if (!idMedia || deferAccessUrl) {
      return
    }

    const timer = setTimeout(() => {
      executeAccessUrlQuery({ variables: { id: idMedia } }).catch(() => {})
    }, ACCESS_URL_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [deferAccessUrl, executeAccessUrlQuery, idMedia])

  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(true)

  if (error) {
    return <GraphqlError error={error} />
  }
  if (!mediaData && (loading || !data)) {
    return <Loading variant="inline" />
  }

  const resolved: MediaInfo = mediaData ?? { ...data.media }
  const { filename, mimeType, name } = resolved
  const mediaName = name + (name !== filename ? ` [${filename}]` : '')

  if (deferAccessUrl) {
    return (
      <span className={classes.nameWithLoadingUrl}>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => {
            openAccessUrl()
          }}
        >
          {mediaName}
        </Link>
        {isOpeningUrl && <Loading variant="inline" />}
      </span>
    )
  }

  const urlEntry = mediaUrlData?.mediaAccessUrl
  const accessUrl = urlEntry?.url
  const urlExpired = urlEntry ? isUrlExpired(urlEntry.issued, urlEntry.expiration) : false
  const urlFetchSettled = !mediaUrlLoading

  if (mediaUrlError || urlExpired || (urlFetchSettled && !accessUrl)) {
    return (
      <span className={classes.urlErrorWrapper}>
        {mediaName}
        <Tooltip title="Retry fetching link">
          <IconButton size="small" onClick={retryUrl}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </span>
    )
  }

  if (mediaUrlLoading || !accessUrl) {
    return (
      <span className={mediaData ? undefined : classes.nameWithLoadingUrl}>
        {mediaName}
        {!mediaData && <Loading variant="inline" />}
      </span>
    )
  }

  const isImage = mimeType.split('/')[0] === 'image'

  return (
    <span>
      {showPreview && isImage ? (
        <a href={accessUrl} rel="noreferrer noopener" target="_blank">
          {isPreviewLoading && <Loading variant="inline" />}
          <img
            alt={mediaName}
            className={classes.imageMediaPreview}
            onLoad={() => setIsPreviewLoading(false)}
            src={accessUrl}
          />
        </a>
      ) : (
        <a href={accessUrl} rel="noreferrer noopener" target="_blank">
          {mediaName}
        </a>
      )}
    </span>
  )
}
